import { useCallback, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { DefaultButton, PrimaryButton } from "@fluentui/react";
import { nanoid } from "nanoid";

import styles from "./FilePicker.module.css";

import { getToken } from "../../authConfig";
import { handleError } from "../../toaster";
import {
  uploadFile, PermissionsGroup, PermissionsUser, StatusLogClassification,
  StatusLogEntry, StatusLogState, UploadFile
} from "../../api"
import { DropZone } from "./DropZone"
import { FilesList } from "./FilesList";

interface Props {
  folderPath: string;
  tags: string[];
  groupPermissions: PermissionsGroup[];
  userPermissions: PermissionsUser[];
  useDarkMode: boolean;
};

export const FilePicker = ({ folderPath, tags, groupPermissions, userPermissions, useDarkMode }: Props) => {
  const client = useMsal().instance;
  const [files, setFiles] = useState<any>([]);
  const [uploadStarted, setUploadStarted] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const pendingFilesCount = files.filter((file: UploadFile) => file.status === "pending").length;
  const folderName = folderPath;

  const allowedFileTypes = [
    // Documents
    'application/pdf',
    'text/html',
    'text/csv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'message/rfc822',
    'text/markdown',
    'application/vnd.ms-outlook',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/xml',
    'application/json',

    // Video
    'video/x-flv',
    'application/mxf',
    'video/MP2T',
    'video/3gpp',
    'video/mpeg',
    'video/x-ms-wmv',
    'video/x-ms-asf',
    'video/x-msvideo',
    'video/mp4',
    'video/x-matroska',

    // Audio
    'audio/wav',
    'audio/x-m4a',
    'audio/mp4',
    'video/quicktime',

    // Images
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/tiff'
  ];

  // handler called when files are selected via the Dropzone component
  const handleOnChange = useCallback((newFiles: FileList) => {
    const newFilesArray = Array.from(newFiles);
    const newFilesArrayMap = newFilesArray.map((file: File) => {
      let status: 'pending' | 'error' = 'pending';
      let statusMessage = 'Pending Upload';

      if (!allowedFileTypes.includes(file.type)) {
        status = 'error';
        statusMessage = 'File type not supported';
      }

      return {
        id: nanoid(),
        file,
        status,
        statusMessage
      };
    });
    setFiles((prevFiles: UploadFile[]) => [...prevFiles, ...newFilesArrayMap]);
    setUploadStarted(false);
    setUploadComplete(false);
  }, []);

  // handle for removing files from the files list view
  const handleClearFile = useCallback((id: any) => {
    setFiles((prev: UploadFile[]) => prev.filter((file: any) => file.id !== id));
  }, []);

  // handle for clearing all files
  const handleClearAll = useCallback(() => {
    setFiles([]);
  }, []);

  // execute the upload operation
  const handleUpload = useCallback(async () => {
    try {
      setUploadStarted(true);

      const selectedGroupIds = groupPermissions.map(group => group.id);
      const selectedUserIds = userPermissions.map(user => user.id);

      const uploadPromises = files.map(async (indexedFile: UploadFile, index: number) => {

        // Skip files that are not 'pending' status
        // Will exclude files already uploaded or errors
        if (indexedFile.status !== 'pending') {
          return;
        }

        // Mark file as uploading
        setFiles((prevFiles: UploadFile[]) => prevFiles.map(f => f.id === indexedFile.id ? { ...f, status: 'uploading', statusMessage: 'Uploading File...' } : f));

        const file = indexedFile.file as File;
        const filePath = folderName === "" ? file.name : `${folderName}/${file.name}`;

        try {
          const logEntry: StatusLogEntry = {
            path: filePath,
            status: "File uploaded from browser to Azure Blob Storage",
            status_classification: StatusLogClassification.Info,
            state: StatusLogState.Uploaded,
            groups: selectedGroupIds,
            users: selectedUserIds,
            tags: tags
          };

          const token = client ? await getToken(client) : undefined;
          await uploadFile(token, file, logEntry);

          // Update the file status to 'success' once upload is done
          setFiles((prevFiles: UploadFile[]) => prevFiles.map((f) => {
            if (f.id === indexedFile.id) {
              return { ...f, status: 'success', statusMessage: 'File uploaded successfully' };
            }
            return f;
          }));
        } catch (error) {
          // Mark file as error
          // Update the file status to 'error'
          setFiles((prevFiles: UploadFile[]) => prevFiles.map((f) => {
            if (f.id === indexedFile.id) {
              return { ...f, status: 'error', statusMessage: `Upload Failed: ${error}` };
            }
            return f;
          }));
          handleError(error, `Upload failed for file: ${indexedFile.file.name}`,);
        }
      });

      await Promise.all(uploadPromises);
      setUploadComplete(true); // Set uploadComplete to true when all uploads are done
    } catch (error) {
      handleError(error, "An error occurred while uploading files");

      // Set all files to error if something fails before the upload loop
      setFiles((prevFiles: UploadFile[]) => prevFiles.map(file => ({
        ...file,
        status: 'error',
        statusMessage: `Upload Failed: ${error}`,
      })));

    } finally {
      setUploadStarted(false);
    }

  }, [files, folderName, tags]);

  return (
    <div className={useDarkMode ? styles.wrapperDark : styles.wrapper}>
      <div className={styles.canvasWrapper}>
        <DropZone
          onChange={handleOnChange}
          accept={allowedFileTypes}
          useDarkMode={useDarkMode}
        />
      </div>

      {files.length ? (
        <div className={styles.filesListWrapper}>
          <FilesList
            files={files}
            onClear={handleClearFile}
          />
          <div className={styles.buttonContainer}>
            <PrimaryButton
              onClick={handleUpload}
              className={`${styles.uploadButton} ${uploadStarted || uploadComplete || pendingFilesCount === 0 ? styles.disabled : ''}`}
              aria-label="Upload Files"
              text={uploadComplete ? "Upload Complete" : `Upload ${pendingFilesCount} Files`}
              disabled={uploadStarted || uploadComplete || pendingFilesCount === 0}
            />
            <DefaultButton
              onClick={handleClearAll}
              className={`${styles.uploadButton} ${uploadStarted ? styles.disabled : ''}`}
              text="Clear All"
              disabled={uploadStarted}
            />
          </div>
        </div>) : null}
    </div>
  );
};